const ACTIVATION_TIMEOUT_MS = 8_000;

/**
 * Reloads the page to pick up a new app version.
 *
 * Without a service worker (dev, Capacitor native) this is a plain reload.
 * With one, a bare reload re-serves the very build the user is trying to leave.
 * The hint lives on `/`, and a navigation to `/` is answered from the precache
 * — workbox reaches the precached `index.html` through its directory-index
 * variation, and that route is registered ahead of the NetworkFirst navigation
 * rule — so the network is never consulted at all. The precache belongs to the
 * *active* worker, which means the old shell comes back instantly and the hint
 * reappears with it. (Sub-paths are the other way round: nothing precached
 * matches them, so they do go through NetworkFirst.) So instead: trigger an SW
 * update check, give the fresh worker a bounded window to take control
 * (skipWaiting + clientsClaim in the SW make that automatic), then reload.
 * Every path ends in a reload; the wait is best-effort.
 */
export function reloadForUpdate(): void {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  void updateServiceWorker()
    .catch(() => undefined)
    .then(() => window.location.reload());
}

async function updateServiceWorker(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  await Promise.race([awaitFreshController(registration), delay(ACTIVATION_TIMEOUT_MS)]);
}

async function awaitFreshController(registration: ServiceWorkerRegistration): Promise<void> {
  await registration.update();
  const incoming = registration.installing ?? registration.waiting;
  if (!incoming) return; // already on the latest SW — reload straight away
  await new Promise<void>((resolve) => {
    navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), {
      once: true,
    });
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
